import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { AppState } from '@chegg-wt/core/state/types'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import {
  ONE_AUTH_MODAL,
  REDESIGNED_UNLOCK_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import {
  openModal,
  closeModal,
  unlockActivityCompleted,
} from '@chegg-wt/core/state/modals/actions'
import {
  writingFormSubmit,
  formSubmitStarted,
  formSubmitStopped,
} from '@chegg-wt/core/state/form/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import {
  OpenModalPayload,
  UnlockCompletePayload,
} from '@chegg-wt/core/state/modals/types'
import { UnlockCompleteActions } from '@chegg-wt/core/components/Modals/UnlockAccountGateModal/types'
import { Config } from '@chegg-wt/core/config'
import usePriceTest from '@chegg-wt/core/hooks/usePriceTest'
import { trackWithoutThrow } from '@chegg-wt/core/utils/errors'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { componentOrigins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { EB_AD_UNLOCK_MODAL } from '@chegg-wt/core/state/optimizely/constants'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { authModalExpFlagSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { useRoutesContext } from '../RoutesContext'
import AppLink from '../AppLink'
import {
  AUTH_MODAL_TITLE,
  AuthModalData,
  AuthModalOrigin,
} from '../Modals/AuthModal/types'
import {
  FOOTER_BANNER_CTA_TEXT,
  FOOTER_BANNER_TEXT,
  UCF_UNLOCK_MODAL_ID,
  SPONSORED_CTA_TEXT,
  SUBSCRIBE_CTA_TEXT,
  UNLOCK_MODAL_TITLE,
  TRY_IT_FOR_TODAY_CTA_TEXT,
  UNLOCK_AD_MODAL_SUBHEADING,
} from './constants'
import {
  HackyModal,
  ModalContents,
  SponsoredCTA,
  SubscribeCTA,
  HackyFooter,
  UnlockModalSubHeading,
} from './styled'
import { SmallNotificationBanner } from './SmallNotificationBanner'
import { SPONSORED_CTA_DATA_TEST, SUBSCRIBE_CTA_DATA_TEST } from './testIds'

interface UCFUnlockModalProps {
  isLoggedIn: boolean
  authModalExpFlag: boolean
  modalData: {
    withAuthBanner: boolean
    alwaysParticipate?: boolean
  }
  openModal: (modalOptions: OpenModalPayload) => void
  closeModal: (name: string, origin?: string) => void
  writingFormSubmit: ({ citation, initialCitationData }) => void
  formSubmitStopped: () => void
  clickSponsoredRioEvent: () => void
  clickSubscribeRioEvent: (value: string) => void
  clickSignInRioEvent: () => void
  modalOpenRioEvent: () => void
  unlockActivityCompleted: (payload: UnlockCompletePayload) => void
  formSubmitStarted: () => void
}

const MODAL_ANIMATION_DURATION_MS = 300

export const UCFUnlockModal: React.FC<UCFUnlockModalProps> = ({
  isLoggedIn,
  openModal,
  closeModal,
  modalData,
  unlockActivityCompleted,
  formSubmitStopped,
  clickSponsoredRioEvent,
  clickSubscribeRioEvent,
  clickSignInRioEvent,
  modalOpenRioEvent,
  formSubmitStarted,
  authModalExpFlag,
}) => {
  const showAuthModal = authModalExpFlag && !isLoggedIn
  const { isEnabled: staticUnlockModalEnabled, clientReady } = useSafeOptimizelyFeature(
    EB_AD_UNLOCK_MODAL
  )

  const { siteName } = Config
  const staticUnlockModalEnabledOnEB =
    clientReady && staticUnlockModalEnabled && siteName === 'EasyBib'

  const { toUpgrade } = useRoutesContext()
  const [isOpen, setIsOpen] = useState(true)
  const [isSAModalOpen, setIsSAModalOpen] = useState(false)
  const [isAdActivityOpen, setIsAdActivityOpen] = useState(false)
  const { price } = usePriceTest()
  const SUBSCRIBE_CTA_TEXT_STATIC = staticUnlockModalEnabledOnEB
    ? TRY_IT_FOR_TODAY_CTA_TEXT
    : SUBSCRIBE_CTA_TEXT(price)
  const backdropClick = () => {
    setIsOpen(false)
  }

  const onSponsoredClick = () => {
    clickSponsoredRioEvent()

    setIsOpen(false)
    setIsAdActivityOpen(true)

    new Promise((resolve) => {
      setTimeout(resolve, MODAL_ANIMATION_DURATION_MS)
    }).then(() =>
      window.SBMGlobal.run
        .showUnlock({ skipStartView: true })
        .catch(trackWithoutThrow)
        .then(() =>
          unlockActivityCompleted({
            completionAction: UnlockCompleteActions.FORM_SUBMIT,
          })
        )
    )
  }

  const onSignInClick = () => {
    formSubmitStarted()
    clickSignInRioEvent()

    setIsOpen(false)
    setIsSAModalOpen(true)
  }

  const onSubscribeClick = () => {
    clickSubscribeRioEvent(SUBSCRIBE_CTA_TEXT_STATIC)
    if (showAuthModal) {
      openModal({
        name: ONE_AUTH_MODAL,
        data: {
          defaultFlow: 'signup',
          origin: AuthModalOrigin.UCF_UNBLOCK_MODAL,
          redirectTo: toUpgrade(),
          title: AUTH_MODAL_TITLE,
        },
      })
    }
  }

  useEffect(() => {
    const isContinuing = isSAModalOpen || isAdActivityOpen
    const isBailingOut = !isOpen && !isContinuing
    if (isBailingOut) {
      formSubmitStopped()
    }
  }, [isOpen, isSAModalOpen, isAdActivityOpen])

  useEffect(() => {
    if (!isSAModalOpen) {
      return
    }

    const asyncOpenId = setTimeout(() => {
      const data: AuthModalData = {
        defaultFlow: 'signin',
        origin: AuthModalOrigin.REDESIGNED_UNLOCK_MODAL,
        alwaysParticipate: modalData.alwaysParticipate,
      }
      openModal({ name: ONE_AUTH_MODAL, data })
    }, MODAL_ANIMATION_DURATION_MS)

    return () => {
      clearTimeout(asyncOpenId)
    }
  }, [isSAModalOpen])

  useEffect(() => {
    if (isOpen) {
      modalOpenRioEvent()
      return
    }

    const asyncCloseId = setTimeout(
      () => !isSAModalOpen && closeModal(REDESIGNED_UNLOCK_MODAL),
      MODAL_ANIMATION_DURATION_MS
    )

    return () => {
      clearTimeout(asyncCloseId)
    }
  }, [isOpen])

  return (
    <ClientOnlyPortal>
      <HackyModal
        id={UCF_UNLOCK_MODAL_ID}
        isOpen={isOpen}
        onClose={backdropClick}
        hideCloseButton
        title={UNLOCK_MODAL_TITLE}
      >
        <>
          <ModalContents staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}>
            <SponsoredCTA
              staticUnlockModalEnabledOnEB={staticUnlockModalEnabledOnEB}
              onClick={onSponsoredClick}
              data-test={SPONSORED_CTA_DATA_TEST}
            >
              {SPONSORED_CTA_TEXT}
            </SponsoredCTA>
            <span>or</span>
            {staticUnlockModalEnabled && (
              <UnlockModalSubHeading>{UNLOCK_AD_MODAL_SUBHEADING}</UnlockModalSubHeading>
            )}
            {showAuthModal ? (
              <SubscribeCTA
                href={undefined}
                data-test={SUBSCRIBE_CTA_DATA_TEST}
                onClick={onSubscribeClick}
              >
                {SUBSCRIBE_CTA_TEXT_STATIC}
              </SubscribeCTA>
            ) : (
              <AppLink route={toUpgrade()}>
                <SubscribeCTA
                  href="I'm needed because TS complains otherwise, AppLink replaces me when rendering"
                  data-test={SUBSCRIBE_CTA_DATA_TEST}
                  onClick={onSubscribeClick}
                >
                  {SUBSCRIBE_CTA_TEXT_STATIC}
                </SubscribeCTA>
              </AppLink>
            )}
          </ModalContents>
          {modalData.withAuthBanner && !isLoggedIn ? (
            <HackyFooter>
              <SmallNotificationBanner
                message={FOOTER_BANNER_TEXT}
                ctaText={FOOTER_BANNER_CTA_TEXT}
                onSignInClick={onSignInClick}
              />
            </HackyFooter>
          ) : null}
        </>
      </HackyModal>
    </ClientOnlyPortal>
  )
}

const mapStateToProps = (state: AppState) => ({
  isLoggedIn: loggedInSelector(state),
  modalData: modalDataSelector(state),
  authModalExpFlag: authModalExpFlagSelector(state),
})

const mapDispatchToProps = {
  openModal,
  closeModal,
  writingFormSubmit,
  formSubmitStopped,
  unlockActivityCompleted,
  formSubmitStarted,
  clickSponsoredRioEvent: () =>
    userClick({
      origin: origins.unlockModalSponsoredClick,
    }),
  clickSubscribeRioEvent: (value) =>
    userClick({
      origin: origins.unlockModalSubscribeClick,
      value,
    }),
  clickSignInRioEvent: () =>
    userClick({
      origin: origins.unlockModalSignInClick,
    }),
  modalOpenRioEvent: () =>
    emitComponentViewEvent({
      origin: componentOrigins.unlockModalOpen,
    }),
}

export default connect(mapStateToProps, mapDispatchToProps)(UCFUnlockModal)
